import useWindowSize from '@propertypal/shared/src/hooks/useWindowSize';
import React, {
  CSSProperties,
  FunctionComponent,
  MouseEventHandler,
  TouchEventHandler,
  useEffect,
  useRef,
  useState,
} from 'react';
import { BackgroundBar, BarBox, Container, FillerBar, Handle, HandleText, Label } from './Slider.style';

interface Props {
  containerStyle?: CSSProperties;
  disabled?: boolean;
  direction?: string;
  appendix: string;
  label: string;
  value: number;
  maxValue: number;
  onValueChange: (value: number) => void;
}

const Slider: FunctionComponent<Props> = (props) => {
  const [value, setValue] = useState(props.value);
  const barRef = useRef<HTMLDivElement>(null);
  const [sliderWidth, setSliderWidth] = useState(0);
  const [translateX, setTranslateX] = useState((props.value / props.maxValue) * sliderWidth);
  const lastTranslateX = useRef<number>(0);
  const lastX = useRef<number | null>(null);
  const windowSize = useWindowSize();

  const onDragStart: MouseEventHandler<HTMLDivElement> = (event) => {
    lastTranslateX.current = translateX;
    lastX.current = event.clientX;
  };

  const onTouchDragStart: TouchEventHandler = (event) => {
    lastTranslateX.current = translateX;
    lastX.current = event.touches[0].clientX;
  };

  const onDrag = (x: number) => {
    if (lastX.current !== null) {
      const diff = lastTranslateX.current - (lastX.current - x);
      const newValue = Math.min(Math.max(0, diff), sliderWidth);
      setValue(Math.round((newValue / sliderWidth) * props.maxValue));
      setTranslateX(newValue);
    }
  };

  const onTouchMove: TouchEventHandler = (event) => {
    onDrag(event.touches[0].clientX);
  };

  const onMouseMove: MouseEventHandler = (event) => {
    onDrag(event.clientX);
  };

  const onDragEnd = () => {
    if (lastX.current !== null) {
      lastX.current = null;
      props.onValueChange(value);
    }
  };

  useEffect(() => {
    if (barRef.current) {
      const newWidth = barRef.current.getBoundingClientRect().width - 80;
      setSliderWidth(newWidth);
      setTranslateX((props.value / props.maxValue) * newWidth);
    }
  }, [windowSize.width]);

  return (
    <Container style={props.containerStyle} direction={props.direction}>
      <Label>{props.label}</Label>

      <BarBox>
        <BackgroundBar disabled={props.disabled} data-testid="slider" ref={barRef}>
          <Handle
            data-testid="slider-handle"
            onMouseDown={onDragStart}
            onMouseMove={onMouseMove}
            onMouseUp={onDragEnd}
            onMouseLeave={onDragEnd}
            onTouchStart={onTouchDragStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onDragEnd}
            onTouchCancel={onDragEnd}
            style={{ transform: `translateX(${translateX}px)` }}
          >
            <HandleText>
              {value} {props.appendix}
            </HandleText>
          </Handle>

          <FillerBar style={{ width: translateX + 10 }} />
        </BackgroundBar>
      </BarBox>
    </Container>
  );
};

export default Slider;
